import { FC, useCallback } from 'react';
import { Box, Button } from '@alf/uikit';
import { useStatuses } from '@namespace/account';
import { LOG_ACTIONS, logAction, useLogAmplitude } from '@namespace/analytics';
import { useConfig, useGeneralRoutes, useOtherSettings } from '@namespace/cms';
import { LocalizedLink, useI18n, useLocation } from '@namespace/i18n';
import {
  useDefineDepositPath,
  useLudomanInVerification
} from '@namespace/user';

import styles from './index.module.css';

interface DepositButtonProps {
  id: string;
  className?: string;
  customDepositPath?: string;
  place?: string;
  amplitudeEntryPoint?: string;
}

export const DepositButton: FC<DepositButtonProps> = ({
  id,
  className = '',
  customDepositPath,
  place = 'header-deposit',
  amplitudeEntryPoint,
  ...rest
}) => {
  const { config = {} } = useConfig(id);
  const { amplitudeEntryPoint: componentAmplitudeEntryPoint } = config;
  const { pathname } = useLocation();
  const { t } = useI18n();
  const depositPath = useDefineDepositPath();
  const otherSettings = useOtherSettings();
  const { wideDepositButton = false } = otherSettings;
  const { showNotification: showLudomanNotification } =
    useLudomanInVerification();
  const logAmplitude = useLogAmplitude();
  const {
    cyberSport,
    profilePage,
    gameLauncher,
    gamePreview,
    casinoLobby,
    pinnacle,
    deposit
  } = useGeneralRoutes();
  const { isNotVerified, isNeedSetupLimits } = useStatuses();
  const handleBoxClick = useCallback(() => {
    showLudomanNotification();
    logAction(LOG_ACTIONS.DEPOSIT_CLICK_HEADER);
  }, [showLudomanNotification]);

  const handleButtonClick = useCallback(() => {
    if (pathname !== `${deposit}/`) {
      logAmplitude({
        actionName: LOG_ACTIONS.DEPOSIT_BUTTON_CLICK,
        attributes: {
          entry_point: componentAmplitudeEntryPoint || amplitudeEntryPoint,
          verified: !isNotVerified,
          deposit_limits_set: !isNeedSetupLimits
        },
        isMatchPath: true,
        additionalRoutes: {
          profile: profilePage,
          cybersport: cyberSport,
          games: [gameLauncher, gamePreview, casinoLobby, pinnacle]
        }
      });
    }
  }, [
    componentAmplitudeEntryPoint,
    amplitudeEntryPoint,
    isNeedSetupLimits,
    isNotVerified,
    logAmplitude,
    pathname
  ]);

  return (
    <Box
      data-role={`${place}-depositButton`}
      className={`${styles.buttonWrapper}`}
      onClick={handleBoxClick}
    >
      <Button
        onClick={handleButtonClick}
        component={LocalizedLink}
        size="s"
        to={customDepositPath || depositPath}
        actionType="color1"
        intent="primary"
        className={`${
          wideDepositButton ? styles.wideDepositButton : ''
        } ${className}`}
        {...rest}
      >
        {t('personalOffice.deposit')}
      </Button>
    </Box>
  );
};
